import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import onDomContentLoaded from '../../utilities/onDomContentLoaded';
import PersonalFinanceContainer from './budget-calculator/PersonalFinanceContainer';
import InstantOffersLoader from '../../components/instant-offers/InstantOffersLoader';
import { instantOffersSectionId } from '../../components/instant-offers/utils/constants';
import SocialProofContainer from './social-proof/SocialProofContainer';
import ResearchArticlesContainer from './research-articles/ResearchArticlesContainer';
import StatePricingDisclaimerContainer from '../../components/StatePricingDisclaimerContainer';
import ErrorBoundary from '../../components/ErrorBoundary';
import * as nearestStoreLinkBuilder from './utilities/nearestStoreLinkBuilder';
import { FULL_STORY_HOME_PAGE_ID } from './utilities/constants';
import '../../../scss/pages/home/main.scss';
import '@kmx/finance-shared-react-components/dist/finance-shared-react-components.css';
import { ServerSideExperimentProvider } from './campaigns/ServerSideExperimentContext';
import { LocalCarsLoader } from './local-cars/LocalCarsLoader';
import { CarRecommendationsLoader } from './car-recommendations/CarRecommendationsLoader';
import { RecentSearchesContainer } from './homebase-optimization-test/recent-searches/RecentSearchesContainer';
import { appState } from '../../common/state/appState';
import { YouMightLikeContainer } from './homebase-optimization-test/YouMightLikeContainer';
import LinkFarm from './link-farm';
import theme from '../../theme';
import HeroContainer from './hero/HeroContainer';
import { TheCarMaxDifference } from '../../components/TheCarMaxDifference';
import { KmxContainer } from '../../components/KmxContainer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WhatKindOfCarsContainer from './what-kind-of-cars/what-kind-of-cars-container';
import LazyLoad from 'react-lazyload';
import QuickLinksContainer from './quick-links/QuickLinksContainer';
import { Header } from '@kmx/header';
import RecommendedCategoriesWrapper from './recommended-categories/RecommendedCategoriesWrapper';
import FinancePromosSection from './finance-promos/FinancePromosSection';
import { createRoot } from 'react-dom/client';

function startup() {
    // If the window.savedVehicleHelper isn't defined,
    // we are never going to get the customers favorites
    // It's not ideal, but wait 5 seconds to see if that loads, otherwise just load the page without the favorites
    const { enableUFTHome } = window.featureFlags;
    const waitForSavedVehicleHelper = (timeout = 5000) => {
        const startTime = Date.now();
        const interval = 100;

        const checkHelper = () => {
            if (window.SavedVehicleHelper) {
                appState.getState().loadHomePageExperience({ enableUFTHome });
            } else if (Date.now() - startTime < timeout) {
                setTimeout(checkHelper, interval);
            } else {
                appState.getState().loadHomePageExperience({ enableUFTHome });
            }
        };

        checkHelper();
    };

    waitForSavedVehicleHelper();
    appState.getState().getIsPreQualified();

    const setHomeOptimizationTestGroup = appState.getState().setHomebaseOptimizationGroup;
    const setOptimizedReturnExperience = appState.getState().setOptimizedReturnExperience;

    nearestStoreLinkBuilder.buildAllLinks();

    document.addEventListener('enableHomeOptimizationTest', (e: Event) => {
        const customEvent = e as CustomEvent;
        setHomeOptimizationTestGroup(customEvent.detail);
    });

    document.addEventListener('enableOptimizedReturnExperience', (e: Event) => {
        const customEvent = e as CustomEvent;
        setOptimizedReturnExperience(customEvent.detail);
    });

    const headerContainer = document.getElementById('kmx-header') as HTMLElement;

    // The header container is conditionally added via feature flags in the backend.
    if (headerContainer) {
        const root = createRoot(headerContainer);
        root.render(
            <ErrorBoundary>
                <ServerSideExperimentProvider>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <Header />
                    </ThemeProvider>
                </ServerSideExperimentProvider>
            </ErrorBoundary>,
        );
    }

    const quickLinksContainer = document.getElementById('quick-links') as HTMLElement;

    if (quickLinksContainer) {
        const root = createRoot(quickLinksContainer);
        root.render(
            <ErrorBoundary>
                <ServerSideExperimentProvider>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <QuickLinksContainer />
                    </ThemeProvider>
                </ServerSideExperimentProvider>
            </ErrorBoundary>,
        );
    }

    const heroContainer = document.getElementById('hero') as HTMLElement;

    if (heroContainer) {
        const root = createRoot(heroContainer);
        root.render(
            <ErrorBoundary>
                <ServerSideExperimentProvider>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <HeroContainer />
                    </ThemeProvider>
                </ServerSideExperimentProvider>
            </ErrorBoundary>,
        );
    }

    const financePromosContainer = document.getElementById('finance-promos') as HTMLElement;

    if (financePromosContainer) {
        const root = createRoot(financePromosContainer);
        root.render(
            <ErrorBoundary>
                <ServerSideExperimentProvider>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <FinancePromosSection />
                    </ThemeProvider>
                </ServerSideExperimentProvider>
            </ErrorBoundary>,
        );
    }

    const recentSearchesContainer = document.getElementById('recent-searches-container');

    if (recentSearchesContainer) {
        const root = createRoot(recentSearchesContainer);
        root.render(
            <ErrorBoundary>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <RecentSearchesContainer />
                </ThemeProvider>
            </ErrorBoundary>,
        );
    }

    if (!enableUFTHome) {
        const youMightLikeContainer = document.getElementById('you-might-like-container');

        if (youMightLikeContainer) {
            const root = createRoot(youMightLikeContainer);
            root.render(
                <ErrorBoundary>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <YouMightLikeContainer />
                    </ThemeProvider>
                </ErrorBoundary>,
            );
        }
    }

    const pfcDomElementWrapper = document.getElementById('budget-calculator-section');

    if (pfcDomElementWrapper) {
        const root = createRoot(pfcDomElementWrapper);
        root.render(
            <ErrorBoundary>
                <ServerSideExperimentProvider>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <PersonalFinanceContainer />
                    </ThemeProvider>
                </ServerSideExperimentProvider>
            </ErrorBoundary>,
        );
    }

    if (enableUFTHome) {
        const recommendedCategoriesContainer = document.getElementById('recommended-categories-section');
        if (recommendedCategoriesContainer) {
            const root = createRoot(recommendedCategoriesContainer);
            root.render(
                <ErrorBoundary>
                    <ServerSideExperimentProvider>
                        <ThemeProvider theme={theme}>
                            <CssBaseline />
                            <RecommendedCategoriesWrapper />
                        </ThemeProvider>
                    </ServerSideExperimentProvider>
                </ErrorBoundary>,
            );
        }
    }

    if (enableUFTHome) {
        const whatKindOfCars = document.getElementById('what-kind-of-cars');
        if (whatKindOfCars) {
            const root = createRoot(whatKindOfCars);
            root.render(
                <ErrorBoundary>
                    <ServerSideExperimentProvider>
                        <ThemeProvider theme={theme}>
                            <CssBaseline />
                            <LazyLoad height={window.innerHeight} once>
                                <WhatKindOfCarsContainer />
                            </LazyLoad>
                        </ThemeProvider>
                    </ServerSideExperimentProvider>
                </ErrorBoundary>,
            );
        }
    }
    const localCars = document.getElementById('local-cars');

    if (localCars) {
        const root = createRoot(localCars);
        root.render(
            <ErrorBoundary>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <LocalCarsLoader />
                </ThemeProvider>
            </ErrorBoundary>,
        );
    }

    const carRecommendations = document.getElementById('car-recommendations');

    if (carRecommendations) {
        const root = createRoot(carRecommendations);
        root.render(
            <ErrorBoundary>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <CarRecommendationsLoader />
                </ThemeProvider>
            </ErrorBoundary>,
        );
    }

    const instantOffersContainer = document.getElementById(instantOffersSectionId);

    if (instantOffersContainer) {
        const root = createRoot(instantOffersContainer);
        root.render(
            <ErrorBoundary>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <InstantOffersLoader />
                </ThemeProvider>
            </ErrorBoundary>,
        );
    }

    const belowTheFoldContainer = document.getElementById('below-the-fold');

    if (belowTheFoldContainer) {
        const root = createRoot(belowTheFoldContainer);
        root.render(
            <ErrorBoundary>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    {/* Using !== true in order to avoid issues with undefined/false/etc */}
                    {window.featureFlags.disableSocialProofCarousel !== true && (
                        <SocialProofContainer
                            fullStoryPageId={FULL_STORY_HOME_PAGE_ID}
                            nextFocus="#research-articles-component"
                        />
                    )}
                    <ResearchArticlesContainer fullStoryPageId={FULL_STORY_HOME_PAGE_ID} />
                    <KmxContainer
                        component="section"
                        id="the-carmax-difference"
                        maxWidth="xl"
                        sx={{ pt: { xs: 8, md: 12 } }}
                    >
                        <Stack direction="column" spacing={{ xs: 2, md: 4 }}>
                            <Typography component="h2" variant="h5" sx={{ typography: { sm: 'h4' } }}>
                                The CarMax difference
                            </Typography>
                            <TheCarMaxDifference maxBadgeSize="lg" />
                        </Stack>
                    </KmxContainer>
                    <LinkFarm />
                    <StatePricingDisclaimerContainer fullStoryPageId={FULL_STORY_HOME_PAGE_ID} />
                </ThemeProvider>
            </ErrorBoundary>,
        );
    }
}

onDomContentLoaded(startup);
